import {
  nodeAtomFamily,
  tablesAppAtom,
  interfacesAppAtom,
  chatbotsAppAtom,
  BuiltInApp,
  updateAssetMetadataAtom,
  emitTimeSavedAtom,
  emitTimeSpentAtom,
  latestTimeSavedAtomFamily,
  isZapNode,
  DefaultNodeData,
  isDefaultNode,
  ZapNodeData,
} from "@zapier/canvas-state";
import { Dropdown, Field, Text, TextInput } from "@zapier/design-system";
import { useAtomValue, useSetAtom } from "jotai";
import { Node } from "@xyflow/react";
import { Suspense, useCallback } from "react";
import { convertToSeconds } from "@zapier/date-canvas";

import { Sidebar } from "../Sidebar";
import { StepTitleField } from "../StepTitleField";
import { DefaultAppTileButton } from "../DefaultAppTileButton";
import useUpdateNode from "../../hooks/useUpdateNode";
import { CreateNewZapButton } from "../CreateNewZapButton";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { isAssetNode } from "../../utils/isAssetNode";
import { Notes } from "../Notes";

import { useSidebarEventHandlers } from "./useSidebarEventHandlers";
import ZapFilter from "./ZapFilter";
import { ZapPreviewContainer } from "./ZapPreviewContainer";
import { ActorTypeahead } from "./ActorTypeahead";
import { AppTypeahead } from "./AppTypeahead";
import styles from "./NodeSidebar.module.css";
import { AssetSelector } from "./AssetSelector";
import { ActorList } from "./ActorList";
import { AppList } from "./AppList";
import { SidebarColorPicker } from "./SidebarColorPicker";

const timeUnits = [
  { value: "seconds", label: "Seconds" },
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

export function NodeSidebar({ id }: { id: string }) {
  const singleSelectedNode = useAtomValue(nodeAtomFamily(id)) as Node<
    DefaultNodeData | ZapNodeData
  >;
  const zapId = isZapNode(singleSelectedNode)
    ? singleSelectedNode?.data.zapId
    : undefined;
  const tablesApp = useAtomValue(tablesAppAtom);
  const interfacesApp = useAtomValue(interfacesAppAtom);
  const chatbotsApp = useAtomValue(chatbotsAppAtom);
  const updateNodeAndSave = useUpdateNode();
  const isAssetNodeType = isAssetNode(singleSelectedNode.data);

  const { handleSubmit, onDeleteStep, onRemoveZap, selectedZap } =
    useSidebarEventHandlers({
      node: singleSelectedNode,
    });

  const onChangeApp = useCallback(
    // Intentionally returning a function that returns a function to avoid calling the function immediately
    // when the button is clicked. This way, the function is only called when the button is clicked.
    (service: BuiltInApp | null) => () => {
      if (service) {
        return updateNodeAndSave({
          id,
          data: {
            // Read by the backend to send to Zap Guesser
            app: service.name,
            services: [
              {
                currentImplementation: {
                  id: service.current_implementation_id,
                },
              },
            ],
          },
        });
      } else {
        // When clicking the X button, service will be null, so delete services and app properties from node.data
        updateNodeAndSave({
          id,
          data: {
            app: undefined,
            services: undefined,
            tableId: undefined,
            projectId: undefined,
            pageId: undefined,
            chatbotId: undefined,
          },
        });
      }
    },
    [updateNodeAndSave, id],
  );

  return (
    <Sidebar heading="Step details" onDeleteStep={onDeleteStep}>
      <div className={styles.sidebarWrapper}>
        <StepTitleField label={singleSelectedNode?.data.label} id={id} />
        {!zapId && (
          <>
            <Field
              label="Zap"
              renderLabel={() => <Text type="SmallPrint2">Zap</Text>}
              renderHelpText={
                singleSelectedNode.type === "default"
                  ? () => (
                      <Text type="SmallPrint1">
                        {isAssetNodeType
                          ? "Selecting a Zap will remove the app option below."
                          : "Selecting a Zap will remove app and color options below."}
                      </Text>
                    )
                  : undefined
              }
              renderInput={(inputProps) => (
                <ZapFilter
                  {...inputProps}
                  onChange={(zap) => {
                    handleSubmit(zap);
                  }}
                  selectedZap={selectedZap}
                />
              )}
            />
            <div className={styles.separator}>
              <Text type="SmallPrint1" color="GrayWarm6">
                OR
              </Text>
            </div>
            <CreateNewZapButton nodeId={singleSelectedNode.id} />
          </>
        )}
        {zapId && (
          <ZapPreviewContainer onRemoveZap={onRemoveZap} zapId={zapId} />
        )}
        {!zapId && isDefaultNode(singleSelectedNode) && (
          <span>
            <AppTypeahead node={singleSelectedNode} />
            <AppList
              nodeId={id}
              services={singleSelectedNode.data.services || []}
              node={singleSelectedNode}
            />
          </span>
        )}
        <AssetSelector nodeId={id} />
        {isDefaultNode(singleSelectedNode) &&
          !singleSelectedNode.data.app &&
          !zapId && (
            <div className={styles.buttons}>
              <DefaultAppTileButton
                onClick={onChangeApp(tablesApp)}
                title="Tables"
                subtitle="Store, transform, and automate your data."
                iconName="navTables"
              />
              <DefaultAppTileButton
                onClick={onChangeApp(interfacesApp)}
                title="Interfaces"
                subtitle="Build custom pages for your team and customers."
                iconName="navInterfaces"
              />
              <DefaultAppTileButton
                onClick={onChangeApp(chatbotsApp)}
                title="Chatbots"
                subtitle="Build custom AI chatbots to scale your business."
                iconName="navAIChatbot"
              />
            </div>
          )}
        {singleSelectedNode.type === "default" && !isAssetNodeType && (
          <SidebarColorPicker node={singleSelectedNode} />
        )}
        <span>
          <ActorTypeahead id={id} />
          <ActorList nodeId={id} actors={singleSelectedNode.data.actors} />
        </span>

        {zapId && (
          <Suspense fallback={null}>
            <ErrorBoundary>
              <TimeSaved zapId={zapId} />
            </ErrorBoundary>
          </Suspense>
        )}

        {!zapId && <TimeSpent nodeId={id} />}

        <span>
          <Notes
            nodeId={singleSelectedNode.id}
            notesJson={singleSelectedNode.data.notesJson}
          />
        </span>
      </div>
    </Sidebar>
  );
}

type Time = {
  value: string;
  unit: string;
};

function TimeSaved({ zapId }: { zapId: string }) {
  const emitTimeSaved = useSetAtom(emitTimeSavedAtom);
  const timeValue = useAtomValue(latestTimeSavedAtomFamily(zapId));
  const updateAssetMetadata = useSetAtom(updateAssetMetadataAtom);

  const selectedItem =
    timeUnits.find((unit) => unit.value === timeValue.unit) || timeUnits[1];

  async function onChangeTimeSaved({ unit, value }: Time) {
    await updateAssetMetadata({
      zapId,
      assetType: "zap",
      metadataValue: {
        unit: unit as "seconds" | "minutes" | "hours" | "days",
        value: value.toString(),
      },
      metadataName: "time_saved",
    });
    emitTimeSaved(convertToSeconds(unit, value));
  }

  return (
    <Field
      label="Time saved"
      renderLabel={() => <Text type="SmallPrint2">Time saved</Text>}
      renderHelpText={() =>
        "An estimate of how long it takes to perform this task manually."
      }
      renderInput={(inputProps) => (
        <span className={styles.timeSavedRow}>
          <TextInput
            {...inputProps}
            onChange={(e) =>
              onChangeTimeSaved({
                unit: selectedItem?.value,
                value: (e.target as HTMLInputElement).value,
              })
            }
            value={timeValue.value}
            // @ts-ignore - TextInput doesn't allow us to set number but it works
            type="number"
          />
          <Dropdown
            items={timeUnits}
            selectedItem={selectedItem}
            onChange={({ value }) =>
              onChangeTimeSaved({
                unit: value,
                value: timeValue.value,
              })
            }
          />
        </span>
      )}
    />
  );
}

function TimeSpent({ nodeId }: { nodeId: string }) {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<DefaultNodeData>;
  const emitTimeSpent = useSetAtom(emitTimeSpentAtom);
  const updateNodeAndSave = useUpdateNode();
  const selectedItem =
    timeUnits.find((unit) => unit.value === node?.data?.timeSpent?.unit) ||
    timeUnits[1];

  const updateMetadataValue = ({ value, unit }: Time) => {
    updateNodeAndSave({
      id: nodeId,
      data: {
        timeSpent: { value, unit },
      },
    });
    emitTimeSpent(convertToSeconds(unit, value));
  };

  return (
    <Field
      label="Time spent"
      renderLabel={() => <Text type="SmallPrint2">Time spent</Text>}
      renderHelpText={() =>
        "An estimate for how long it would take to perform this task manually."
      }
      renderInput={(inputProps) => (
        <span className={styles.timeSavedRow}>
          <TextInput
            {...inputProps}
            onChange={(event) =>
              updateMetadataValue({
                value: (event?.target as HTMLInputElement).value,
                unit: node?.data.timeSpent?.unit || selectedItem.value,
              })
            }
            defaultValue={node?.data?.timeSpent?.value}
            // @ts-ignore - TextInput doesn't allow us to set number but it works
            type="number"
          />
          <Dropdown
            items={timeUnits}
            selectedItem={selectedItem}
            onChange={({ value }) =>
              updateMetadataValue({
                unit: value,
                value: node?.data.timeSpent?.value || "0",
              })
            }
          />
        </span>
      )}
    />
  );
}
